import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import BasePopup, { PopupProps as SPopupProps } from "../shared/popup";
import { useConfirm, usePopup } from "providers/PopupProvider";
import {
  CommandPanelToolbar,
  CommandPanelToolbarProps as BaseCommandPanelToolbarProps,
} from "components/CommandPanelToolbar";
import useTranslation from "utils/hooks/useTranslation";

export type CommandPanelToolbarProps = Omit<
  BaseCommandPanelToolbarProps,
  "saveAndClose"
> & {
  saveAndClose?: (close: () => any) => any;
};

export type EntityPopupProps = PopupProps & { id?: number; copy?: boolean };
export type PopupProps = Omit<SPopupProps, "onClose" | "children"> & {
  popupKey?: string;
  isLast?: boolean;
  global?: boolean;
  onClose?: (onClose: () => void, params?: any) => void;
  confirmOnClose?: boolean;
  commandPanelProps?: CommandPanelToolbarProps;
  index?: number;
  translateTitle?: boolean;
  toolbar?:
    | React.ReactNode
    | ((args: { onClose: SPopupProps["onClose"] }) => React.ReactElement);
  children?:
    | ReactNode
    | ((params: { onClose: SPopupProps["onClose"] }) => React.ReactElement);
  isDisabledMode?: boolean;
};

export const Popup: React.FC<PopupProps> = ({
  confirmOnClose,
  onClose: outerOnClose,
  children,
  commandPanelProps,
  toolbar,
  translateTitle,
  isLast,
  isDisabledMode,
  global,
  popupKey,
  ...rest
}) => {
  const { closePopup, closeLastPopup } = usePopup(global);
  const { confirm } = useConfirm(global);
  const { t } = useTranslation();
  const elRef = useRef<any>(null);

  let title = rest.title;

  if (translateTitle && title) {
    title = t(title);
  }

  const close = useCallback(
    (params?: any) => {
      if (!popupKey) {
        outerOnClose
          ? outerOnClose(() => closeLastPopup(), params)
          : closeLastPopup();
        return;
      }
      outerOnClose
        ? outerOnClose(() => closePopup(popupKey), params)
        : closePopup(popupKey);
    },
    [closeLastPopup, closePopup, outerOnClose, popupKey],
  );

  const onClose = useCallback(
    (params?: any) => {
      if (confirmOnClose) {
        confirm({
          message: t("#000002058"),
          positiveButtonText: t("yes"),
          negativeButtonText: t("no"),
          positiveCallback: () => {
            close(params);
          },
        });
      } else {
        close(params);
      }
    },
    [close, confirm, confirmOnClose, t],
  );

  useEffect(() => {
    const escapeEventHandler = (e: KeyboardEvent) => {
      if (isLast && e.key === "Escape" && elRef.current?.isConnected) {
        onClose();
      }
    };
    window.addEventListener("keyup", escapeEventHandler);

    return () => {
      window.removeEventListener("keyup", escapeEventHandler);
    };
  }, [isLast, onClose]);

  const normalizedToolbar = useMemo(() => {
    if (!toolbar) {
      return null;
    }
    if (typeof toolbar === "function") {
      return toolbar({ onClose });
    } else {
      return toolbar;
    }
  }, [onClose, toolbar]);

  return (
    <BasePopup title={title} onClose={onClose} {...rest}>
      <view
        data-scroll=""
        ref={elRef} //style={{ overflowY: "scroll" }}
      >
        {typeof children === "function" ? children({ onClose }) : children}
      </view>
      {commandPanelProps ? (
        <CommandPanelToolbar
          {...commandPanelProps}
          saveAndClose={
            commandPanelProps?.saveAndClose
              ? () => {
                  if (commandPanelProps?.saveAndClose) {
                    commandPanelProps.saveAndClose(close);
                  }
                }
              : undefined
          }
          isDisabledMode={isDisabledMode}
        />
      ) : null}
      {normalizedToolbar}
    </BasePopup>
  );
};
